/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import {
    ContentInfoCardKernel,
    InfoCardKernel,
    InfoCardKernelType,
    RecordingInfoCardKernel,
    UnifiedCollectionInfoCardKernel,
} from 'client-code/actions';

import {
    IInfoCard,
    IInfoCardProperty,
    InfoCardManager,
    InfoCardUserInputHandler,
} from '../infocard';
import { NdvrInteractor } from '../interactors';
import { MyShowItemType } from '../types/TMyShowsItemList';

export class MyShowsInfoCardUserInputHandler extends InfoCardUserInputHandler {
    private readonly MAX_ITEM_IN_PAGE = 13;

    private mInfoCard: IInfoCard = InfoCardManager.getInstance();

    private async horizontalKeyNavigationHelper(direction: 'left' | 'right'): Promise<void> {
        const { activeItemIndex, initialPageOffset, totalItemCount, initialPageList, filter } =
            this.mInfoCard.getContextData(MyShowsInfoCardUserInputHandler.name) || {};

        const nextItemIndex = (direction === 'left' ? -1 : 1) + activeItemIndex;
        if (nextItemIndex < 0 || nextItemIndex >= totalItemCount) return;

        let selectedItem: MyShowItemType;
        const isItemInInitialPage =
            nextItemIndex >= initialPageOffset &&
            nextItemIndex <= initialPageOffset + this.MAX_ITEM_IN_PAGE - 1;
        if (isItemInInitialPage) selectedItem = initialPageList[nextItemIndex - initialPageOffset];
        else {
            const service = new NdvrInteractor();
            const response = await service.getMyShowsItemList({
                myShowsFilter: filter,
                offset: nextItemIndex,
                count: 1,
            });
            [selectedItem] = response.myShows;
        }

        const tActiveItemIndex = nextItemIndex;
        this.mInfoCard.hideActionButtons();
        this.mInfoCard.setProperties({
            hasItemsBefore: tActiveItemIndex > 0,
            hasItemsAfter: tActiveItemIndex < totalItemCount - 1,
        });
        this.mInfoCard.setContextData(MyShowsInfoCardUserInputHandler.name, {
            activeItemIndex: tActiveItemIndex,
            initialPageOffset,
            totalItemCount,
            initialPageList,
            filter,
        });
        this.mInfoCard.setInfoCardKernel(this.getInfoCardKernelForMyshows(selectedItem));
    }

    /**
     * @override
     */
    public onKeyLeft(properties: IInfoCardProperty): void {
        if (!properties.hasItemsBefore) return;
        this.horizontalKeyNavigationHelper('left');
    }

    /**
     * @override
     */
    public onKeyRight(properties: IInfoCardProperty): void {
        if (!properties.hasItemsAfter) return;
        this.horizontalKeyNavigationHelper('right');
    }

    // eslint-disable-next-line class-methods-use-this
    public getInfoCardKernelForMyshows(item: MyShowItemType): InfoCardKernel {
        // TODO : Handle special folder(eg. Recently deleted) when it will be ingested
        let kernel: InfoCardKernel;
        if (item.recordingId) {
            kernel = {
                infoCardKernelType: InfoCardKernelType.RecordingInfoCardKernel,
                recordingId: item.recordingId,
            } as RecordingInfoCardKernel;
        } else if (item.contentId) {
            kernel = {
                infoCardKernelType: InfoCardKernelType.ContentInfoCardKernel,
                contentId: item.contentId,
            } as ContentInfoCardKernel;
        } else {
            kernel = {
                infoCardKernelType: InfoCardKernelType.UnifiedCollectionInfoCardKernel,
                unifiedCollectionId: item.unifiedCollectionId,
            } as UnifiedCollectionInfoCardKernel;
        }

        return kernel;
    }
}
